/**
 * The autonomy mode as a chip. Owners and admins open a menu to change it (full autonomy asks
 * first); everyone else sees it read-only with what it means in the tooltip.
 */
import { ChevronDown, ShieldCheck, ShieldAlert } from '@orgus/ui/icons';
import { useState } from 'react';
import { AUTONOMY_DESCRIPTION, AUTONOMY_LABEL, AUTONOMY_MODES } from '@orgus/protocol';
import type { AutonomyMode } from '@orgus/protocol';
import {
  Badge,
  Button,
  ConfirmDialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  Tooltip,
  toUiError,
} from '@orgus/ui';
import { useCan } from '../auth/useMe.ts';
import { saveAssistantSettings } from './data.ts';

export function AutonomyMenu({ mode }: { mode: AutonomyMode }) {
  const canChange = useCan('admin');
  // The hub lets only an owner turn on full autonomy.
  const isOwner = useCan('owner');
  const [confirmFull, setConfirmFull] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const Icon = mode === 'full' ? ShieldAlert : ShieldCheck;

  const change = async (next: AutonomyMode) => {
    if (next === mode) return;
    setSaving(true);
    setError(null);
    try {
      await saveAssistantSettings({ mode: next });
    } catch (e) {
      const u = toUiError(e);
      setError([u.what, u.why, u.next].filter(Boolean).join(' '));
      throw e;
    } finally {
      setSaving(false);
    }
  };

  if (!canChange) {
    return (
      <Tooltip content={`${AUTONOMY_DESCRIPTION[mode]} Only an owner or admin can change it.`}>
        <span tabIndex={0} className="inline-flex rounded-pill">
          <Badge tone={mode === 'full' ? 'waiting' : 'neutral'} icon={<Icon />}>
            {AUTONOMY_LABEL[mode]}
          </Badge>
        </span>
      </Tooltip>
    );
  }

  return (
    <span className="inline-flex items-center gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="secondary"
            size="sm"
            icon={<Icon />}
            iconRight={<ChevronDown className="max-sm:hidden" />}
            loading={saving}
            aria-label={`Autonomy: ${AUTONOMY_LABEL[mode]}. Change autonomy`}
            title={AUTONOMY_LABEL[mode]}
          >
            {/* An icon on a phone; the name is in the label and the menu. */}
            <span className="max-sm:sr-only">{AUTONOMY_LABEL[mode]}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-80">
          <DropdownMenuLabel>Autonomy for every conversation</DropdownMenuLabel>
          <DropdownMenuRadioGroup
            value={mode}
            onValueChange={(v) => {
              const next = v as AutonomyMode;
              if (next === 'full') setConfirmFull(true);
              else void change(next).catch(() => undefined);
            }}
          >
            {AUTONOMY_MODES.map((m) => (
              <DropdownMenuRadioItem key={m} value={m} disabled={m === 'full' && !isOwner && mode !== 'full'} className="h-auto items-start py-2">
                <span className="flex min-w-0 flex-col">
                  <span className="text-ui text-fg">{AUTONOMY_LABEL[m]}</span>
                  <span className="whitespace-normal text-caption text-fg-subtle">
                    {AUTONOMY_DESCRIPTION[m]}
                    {m === 'full' && !isOwner ? ' Only an owner can turn it on.' : ''}
                  </span>
                </span>
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      {error && (
        <span role="alert" className="max-w-xs truncate text-caption text-status-danger-ink" title={error}>
          {error}
        </span>
      )}
      <ConfirmDialog
        open={confirmFull}
        onOpenChange={setConfirmFull}
        title="Turn on full autonomy?"
        description="It runs every change without asking, including shell commands, policy, secrets and sign-in."
        confirmLabel="Turn on full autonomy"
        cancelLabel="Keep asking"
        tone="danger"
        onConfirm={() => change('full')}
      />
    </span>
  );
}
